// RightColumn.js

import React, { useRef } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachFileIcon from '@mui/icons-material/AttachFile';

export default function RightColumn({
  fromRecipient,
  setFromRecipient,

  toRecipients,
  ccRecipients,
  bccRecipients,
  showBcc,
  setShowBcc,

  emailSubject,
  setEmailSubject,
  emailBody,
  setEmailBody,
  attachment,
  setAttachment,
  selectedFile,
  setSelectedFile,

  contactToUse,
  setContactToUse,
  emailedCount,
  rejectedCount,

  handleOpenNewWindow,
  handleClearEmail,
  handleSendEmail,
  canSend,

  emailSender,
  setEmailSender,

  openPicker,
  renderRecipients
}) {
  const fileInputRef = useRef(null);

  // user clicks paperclip => open file input
  function handleAttachmentClick() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  // once a file is chosen => store name in 'attachment'
  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setAttachment(file.name);
    }
  }

  return (
    <div className="bg-white border border-gray-300 rounded p-4 shadow-sm flex-grow flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-md font-semibold text-gray-800">E-Mail Feature</h2>
        <div className="space-x-2 flex items-center">
          <button
            onClick={handleAttachmentClick}
            className="text-gray-700 hover:text-black p-1"
            title="Attach a File"
          >
            <AttachFileIcon />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <button
            onClick={handleClearEmail}
            className="text-gray-700 hover:text-red-600 p-1"
            title="Clear Email"
          >
            <DeleteIcon />
          </button>

          <button
            onClick={handleOpenNewWindow}
            className="text-gray-700 hover:text-black p-1"
            title="Open in New Window"
          >
            <OpenInNewIcon />
          </button>
        </div>
      </div>

      {attachment && (
        <div className="mb-2">
          <span className="text-xs text-gray-600">
            Attached: <strong>{attachment}</strong>
          </span>
        </div>
      )}

      {/* FROM line */}
      <div className="flex items-center mb-1">
        <button
          onClick={() => openPicker('from')}
          className="bg-gray-200 text-black px-2 py-1 rounded mr-2"
        >
          From
        </button>
        <div className="flex-1 border-b border-gray-300 pb-1">
          {fromRecipient ? (
            <span className="bg-gray-100 px-2 py-1 rounded text-xs mr-1 mb-1 inline-block">
              {fromRecipient.name || fromRecipient.email}
            </span>
          ) : (
            <span className="text-xs text-gray-400">No “From” selected</span>
          )}
        </div>
      </div>

      {/* TO line */}
      <div className="flex items-center mb-1">
        <button
          onClick={() => openPicker('to')}
          className="bg-gray-200 text-black px-2 py-1 rounded mr-2"
        >
          To
        </button>
        <div className="flex-1 border-b border-gray-300 pb-1">
          {renderRecipients(toRecipients)}
        </div>
        <span
          className="ml-2 text-xs text-blue-500 hover:underline cursor-pointer"
          onClick={() => setShowBcc(!showBcc)}
        >
          Bcc
        </span>
      </div>

      {/* CC line */}
      <div className="flex items-center mb-1">
        <button
          onClick={() => openPicker('cc')}
          className="bg-gray-200 text-black px-2 py-1 rounded mr-2"
        >
          Cc
        </button>
        <div className="flex-1 border-b border-gray-300 pb-1">
          {renderRecipients(ccRecipients)}
        </div>
      </div>

      {/* BCC line => only if showBcc */}
      {showBcc && (
        <div className="flex items-center mb-1">
          <button
            onClick={() => openPicker('bcc')}
            className="bg-gray-200 text-black px-2 py-1 rounded mr-2"
          >
            Bcc
          </button>
          <div className="flex-1 border-b border-gray-300 pb-1">
            {renderRecipients(bccRecipients)}
          </div>
        </div>
      )}

      {/* Subject */}
      <label className="block text-xs font-semibold text-gray-700 mt-2 mb-1">
        Subject:
      </label>
      <input
        type="text"
        className="border border-gray-300 w-full p-1 text-sm rounded mb-2"
        placeholder="Add a subject"
        value={emailSubject}
        onChange={e => setEmailSubject(e.target.value)}
      />

      {/* Body */}
      <label className="block text-xs font-semibold text-gray-700">
        Email Body:
      </label>
      <textarea
        className="border border-gray-300 w-full rounded p-2 text-sm resize-none mt-1 flex-grow"
        style={{ minHeight: '120px' }}
        placeholder="Type your message..."
        value={emailBody}
        onChange={e => setEmailBody(e.target.value)}
      />

      {/* Additional fields */}
      <div className="mt-3 flex items-center space-x-2 text-xs">
        <label className="text-gray-700">Contact:</label>
        <select
          className="border rounded p-1 text-xs"
          value={contactToUse}
          onChange={e => setContactToUse(e.target.value)}
        >
          <option>Main Contact</option>
          <option>Billing Contact</option>
          <option>Repair Contact</option>
        </select>

        <label className="text-gray-700"># Emailed:</label>
        <span className="px-2">{emailedCount}</span>

        <label className="text-gray-700"># Rejected:</label>
        <span className="px-2">{rejectedCount}</span>
      </div>

      {/* Send */}
      <div className="mt-4">
        <button
          onClick={handleSendEmail}
          disabled={!canSend}
          className={`${
            canSend
              ? 'bg-yellow-400 hover:bg-yellow-500'
              : 'bg-gray-300 cursor-not-allowed'
          } text-black font-semibold px-4 py-2 rounded text-sm`}
        >
          Send E-Mail
        </button>
      </div>
    </div>
  );
}